import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, CalendarX, Repeat } from "lucide-react";
import { WEEKDAYS, fmtLong } from "@/lib/vigilanza";

const err = (e) => toast.error(e.response?.data?.detail || "Errore");
const sel = "h-9 rounded-md border border-stone-200 bg-white px-2 text-sm";

export function ExclusionsTab({ slots, exclusions }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ kind: "date", date: "", date_to: "", weekday: 5, slot_id: "", note: "" });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["vig-exclusions"] }); qc.invalidateQueries({ queryKey: ["vig-shifts"] }); };
  const addMut = useMutation({
    mutationFn: async () => (await api.post("/vigilanza/exclusions", {
      kind: f.kind, slot_id: f.slot_id || null, note: f.note || null,
      ...(f.kind === "date" ? { date: f.date, date_to: f.date_to || null } : { weekday: Number(f.weekday) }),
    })).data,
    onSuccess: () => { toast.success("Esclusione aggiunta"); setF({ ...f, date: "", date_to: "", note: "" }); refresh(); }, onError: err,
  });
  const delMut = useMutation({ mutationFn: async (id) => api.delete(`/vigilanza/exclusions/${id}`), onSuccess: refresh, onError: err });
  const slotName = (id) => (id ? slots.find((s) => s.id === id)?.name || "—" : "Tutte le fasce");

  return (
    <div className="space-y-5 mt-4">
      <Card className="p-4 border border-stone-200 shadow-none rounded-xl flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Tipo</Label>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={`${sel} block`} data-testid="vig-ex-kind">
            <option value="date">Data o periodo</option><option value="weekday">Giorno fisso della settimana</option>
          </select></div>
        {f.kind === "date" ? (
          <>
            <div><Label className="text-xs">Dal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-40" data-testid="vig-ex-date"/></div>
            <div><Label className="text-xs">Al (facoltativo)</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="w-40" data-testid="vig-ex-date-to"/></div>
          </>
        ) : (
          <div><Label className="text-xs">Ogni</Label>
            <select value={f.weekday} onChange={(e) => setF({ ...f, weekday: e.target.value })} className={`${sel} block`} data-testid="vig-ex-weekday">
              {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
            </select></div>
        )}
        <div><Label className="text-xs">Fascia</Label>
          <select value={f.slot_id} onChange={(e) => setF({ ...f, slot_id: e.target.value })} className={`${sel} block`} data-testid="vig-ex-slot">
            <option value="">Tutte le fasce</option>
            {slots.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></div>
        <div className="flex-1 min-w-[180px]"><Label className="text-xs">Nota</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="es. Vacanze di Natale" data-testid="vig-ex-note"/></div>
        <Button onClick={() => addMut.mutate()} disabled={f.kind === "date" && !f.date} className="bg-[#1C352D] hover:bg-[#2a4a40]" data-testid="vig-ex-add"><Plus size={14} className="mr-1"/> Escludi</Button>
      </Card>

      {exclusions.length === 0 && <div className="text-sm text-stone-400 text-center py-8">Nessun giorno escluso.</div>}
      <div className="grid gap-2">
        {exclusions.map((x) => (
          <Card key={x.id} className="p-3 border border-stone-200 shadow-none rounded-xl flex items-center gap-3" data-testid={`vig-ex-row-${x.id}`}>
            {x.kind === "weekday" ? <Repeat size={15} className="text-[#E2725B]"/> : <CalendarX size={15} className="text-stone-500"/>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-stone-800">
                {x.kind === "weekday" ? `Ogni ${WEEKDAYS[x.weekday].toLowerCase()}` : x.date_to && x.date_to !== x.date ? `Dal ${fmtLong(x.date)} al ${fmtLong(x.date_to)}` : fmtLong(x.date)}
              </div>
              <div className="text-xs text-stone-500">{slotName(x.slot_id)}{x.note ? ` · ${x.note}` : ""}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => delMut.mutate(x.id)} data-testid={`vig-ex-del-${x.id}`}><Trash2 size={14} className="text-red-600"/></Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
